<script setup lang="ts">
import { ref, computed, unref } from 'vue';
import { convertBytes, FileUnits, MULTI_DOC_MAX_FILES_PER_APPOINTMENT } from '@nova/core';
import { useHttpClient } from '@satellite/composables/useHttpClient';
import {
  isHttpErrorWithMessage,
  type DocumentUploadButtonEmits,
  type DocumentUploadButtonProps,
  type StorageResponse,
  type FailedUpload,
  type UploadResult
} from './types';

const props = withDefaults(defineProps<DocumentUploadButtonProps>(), {
  warehouseId: '',
  storageEndpoint: '',
  modelValue: undefined,
  enableCropping: false,
  multiple: false,
  variant: 'default',
  readOnly: false,
  label: 'Upload file',
  placeholder: 'Choose file...',
  conversionUnit: 'KB',
  maxSize: null,
  maxFiles: null
});

const emit = defineEmits<DocumentUploadButtonEmits>();

const httpClient = useHttpClient();
const fileInput = ref<File | File[] | null>(null);
const loading = ref(false);
const errorMessages = ref<string[]>([]);
/** Files that failed to upload; kept so user can retry without re-selecting. */
const failedFiles = ref<File[]>([]);

const acceptedMimeTypes = computed(() => props.allowedMimeTypes?.join(',') ?? undefined);

const conversionUnitEnum = computed((): FileUnits => {
  const u = (props.conversionUnit ?? 'KB').toUpperCase();
  return u === 'MB' ? FileUnits.MB : FileUnits.KB;
});

/** Matches the exact /public/storage path segment (public upload endpoint template). */
const PUBLIC_STORAGE_PATH_REGEX = /\/public\/storage(?:\/|$)/;

function getUploadUrl(): string {
  const base = (props.storageEndpoint ?? '').replace(/\/$/, '');
  if (!base) return '';

  const isPublicStorageEndpoint = PUBLIC_STORAGE_PATH_REGEX.test(base);
  if (isPublicStorageEndpoint) {
    if (!props.warehouseId) {
      throw new Error('warehouseId is required for public storage endpoint');
    }
    const baseWithoutStorage = base.replace(/\/storage\/?$/, '');
    return `${baseWithoutStorage}/${props.warehouseId}/storage`;
  }

  // Authenticated endpoint (e.g. /storage): use as-is
  return base;
}

function validateFileSize(file: File): boolean {
  if (props.maxSize == null || props.maxSize <= 0) return true;
  const sizeInUnit = convertBytes(file.size, conversionUnitEnum.value);
  if (sizeInUnit > props.maxSize) {
    const unit = props.conversionUnit ?? 'KB';
    errorMessages.value = [`File size exceeds limit of ${props.maxSize} ${unit}`];
    return false;
  }
  return true;
}

function validateFileType(file: File): boolean {
  if (!props.allowedMimeTypes?.length) return true;
  const type = (file.type ?? '').toLowerCase();
  const allowed = props.allowedMimeTypes.map(m => m.toLowerCase());
  const isAllowed = allowed.some(
    m => type === m || (m.endsWith('*') && type.startsWith(m.slice(0, -1)))
  );
  if (!isAllowed) {
    errorMessages.value = ['File type is not allowed'];
    return false;
  }
  return true;
}

function validateMaxFiles(files: File[]): boolean {
  if (!props.multiple || files.length === 0) return true;
  const existingCount = Array.isArray(props.modelValue) ? props.modelValue.length : 0;
  const maxAllowed = props.maxFiles ?? MULTI_DOC_MAX_FILES_PER_APPOINTMENT;
  if (existingCount + files.length > maxAllowed) {
    errorMessages.value = [`Maximum of ${maxAllowed} files allowed`];
    return false;
  }
  return true;
}

function getErrorMessage(err: unknown): string {
  const error = err instanceof Error ? err : new Error(String(err));
  if (isHttpErrorWithMessage(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === 'string') return msg;
  }
  return error.message ?? 'Upload failed';
}

async function uploadFile(file: File): Promise<string> {
  const url = getUploadUrl();
  if (!url) throw new Error('Upload URL not configured');

  const formData = new FormData();
  formData.append('file', file);

  const response = await httpClient.post<StorageResponse>(url, formData);

  const data = response.data;
  if (!data?.key) throw new Error('Invalid response: missing key');
  return data.key;
}

async function runUploads(fileList: File[]): Promise<UploadResult> {
  const uploadPromises = fileList.map(file => uploadFile(file));
  const results = await Promise.allSettled(uploadPromises);

  const keys: string[] = [];
  const failed: FailedUpload[] = [];

  results.forEach((result, index) => {
    const file = fileList[index];
    if (result.status === 'fulfilled') {
      keys.push(result.value);
    } else {
      failed.push({ file, error: getErrorMessage(result.reason) });
    }
  });

  return { keys, failed };
}

function buildFailedUploadMessage(failed: FailedUpload[]): string {
  if (failed.length === 0) return '';
  if (failed.length === 1) return failed[0].error;
  const failedNames = failed.map(f => f.file.name).join(', ');
  return `${failed.length} file(s) could not be uploaded (${failedNames}). Please try again.`;
}

function reportFailedUploads(failed: FailedUpload[]): void {
  const message = buildFailedUploadMessage(failed);
  errorMessages.value = [message];
  emit('error', new Error(message));
}

async function handleFileSelect(files: File | File[] | null) {
  fileInput.value = files;
  errorMessages.value = [];
  failedFiles.value = [];

  if (!files) {
    emit('update:model-value', props.multiple ? [] : '');
    emit('cleared');
    return;
  }

  const fileList = Array.isArray(files) ? files : [files];

  if (fileList.length === 0) {
    fileInput.value = null;
    emit('update:model-value', props.multiple ? [] : '');
    return;
  }

  if (!validateMaxFiles(fileList)) {
    fileInput.value = null;
    emit('error', new Error(errorMessages.value[0]));
    return;
  }

  for (const file of fileList) {
    if (!validateFileSize(file) || !validateFileType(file)) {
      fileInput.value = null;
      emit('error', new Error(errorMessages.value[0]));
      return;
    }
  }

  loading.value = true;
  emit('uploading');
  fileInput.value = null;

  const { keys, failed } = await runUploads(fileList);

  if (keys.length > 0) {
    if (props.multiple) {
      const existing = Array.isArray(props.modelValue) ? props.modelValue : [];
      emit('update:model-value', [...existing, ...keys]);
    } else {
      emit('update:model-value', keys[0]);
    }
  }

  if (failed.length > 0) {
    failedFiles.value = failed.map(f => f.file);
    reportFailedUploads(failed);
  }

  if (failed.length === 0) {
    emit('uploaded');
  }

  loading.value = false;
}

async function handleRetryFailed() {
  const toRetry = failedFiles.value;
  if (toRetry.length === 0) return;

  loading.value = true;
  emit('uploading');
  errorMessages.value = [];

  const { keys, failed } = await runUploads(toRetry);

  if (keys.length > 0) {
    if (props.multiple) {
      const existing = Array.isArray(props.modelValue) ? props.modelValue : [];
      emit('update:model-value', [...existing, ...keys]);
    } else {
      emit('update:model-value', keys[0]);
    }
  }

  failedFiles.value = failed.map(f => f.file);

  if (failed.length > 0) {
    reportFailedUploads(failed);
  } else {
    emit('uploaded');
  }

  loading.value = false;
}

function handleClear() {
  fileInput.value = null;
  errorMessages.value = [];
  failedFiles.value = [];
  emit('update:model-value', props.multiple ? [] : '');
  emit('cleared');
}

function handleRemove(index: number) {
  if (Array.isArray(props.modelValue)) {
    const newValue = [...props.modelValue];
    newValue.splice(index, 1);
    emit('update:model-value', newValue);
  }
}
</script>

<template>
  <div class="document-upload-button">
    <v-file-input
      :model-value="unref(fileInput)"
      :label="label"
      :placeholder="placeholder"
      :readonly="readOnly"
      :disabled="unref(loading)"
      :variant="variant === 'simple' ? 'outlined' : 'filled'"
      :accept="unref(acceptedMimeTypes)"
      :multiple="multiple"
      :error-messages="unref(errorMessages)"
      density="comfortable"
      hide-details="auto"
      @update:model-value="handleFileSelect"
      @click:clear="handleClear">
      <template v-if="loading" #append>
        <v-progress-circular indeterminate size="24" width="2" />
      </template>
    </v-file-input>

    <!-- Retry failed uploads (only failed files are re-uploaded) -->
    <div
      v-if="unref(failedFiles).length > 0 && !unref(loading)"
      class="document-upload-button__retry mt-2">
      <v-btn
        size="small"
        variant="tonal"
        color="primary"
        :disabled="readOnly"
        @click="handleRetryFailed">
        Retry {{ unref(failedFiles).length }} failed file{{
          unref(failedFiles).length === 1 ? '' : 's'
        }}
      </v-btn>
    </div>

    <!-- Preview: existing file keys (display as filename from key or key) -->
    <div v-if="modelValue" class="document-upload-button__preview mt-2">
      <v-chip
        v-if="!multiple && typeof modelValue === 'string'"
        closable
        :disabled="readOnly"
        @click:close="handleClear">
        {{ modelValue.split('/').pop() ?? modelValue }}
      </v-chip>
      <div v-else-if="multiple && Array.isArray(modelValue)" class="d-flex flex-wrap gap-2">
        <v-chip
          v-for="(fileKey, index) in modelValue"
          :key="index"
          closable
          :disabled="readOnly"
          @click:close="handleRemove(index)">
          {{ fileKey.split('/').pop() ?? fileKey }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.document-upload-button {
  width: 100%;

  &__preview {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>
